import { ReactNode, useMemo, useState } from "react";
import { useOptionalCombineFilters } from "utils/useCombineFilters";
import type { AppliedFilter } from "components/Shared/FilterEditor/utils";
import { useGraphileFiltersUrlQuery } from "components/Shared/FilterEditor/urlQuerySerializers";
import { RetailerFilter, RetailerType } from "__generated__/gql/graphql";
import FilterBarWithSearch, {
  FilterBarWithSearchProps,
  FreestandingFilterBarContainer,
  FreestandingFilterWell,
} from "components/Shared/FilterEditor/FilterBar/FilterBarWithSearch";
import getResults from "components/Shared/FilterEditor/getResults";
import getFilterDefinitions from "components/Shared/RetailerDirectory/filterDefinitions";
import { RETAILER_TYPE_FILTER_ID } from "components/Shared/RetailerDirectory/RetailerTypeCheckboxes";
import { FREESTANDING_TABLE_CONTAINER_PROPS } from "components/Shared/ChakraReactTable/FullPageTable";
import { IS_ARCHIVED_FILTER_NAME } from "components/Shared/ArchivedStatusButtonGroup";
import { INCLUDE_CUSTOM_FILTER_NAME } from "components/Shared/IncludeCustomFilter";
import FilterBarControls from "./FilterBarControls";
import AdminManageRetailersTable, {
  AdminManageRetailersTableProps,
} from "./Table";

const filterDefinitions = getFilterDefinitions({ isAdmin: true });

const DEFAULT_FILTERS: AppliedFilter[] = [
  { filter: IS_ARCHIVED_FILTER_NAME, value: false, variant: "equalTo" },
  {
    filter: RETAILER_TYPE_FILTER_ID,
    value: [RetailerType.CHAIN, RetailerType.AFFILIATION],
    variant: "in",
  },
  { filter: INCLUDE_CUSTOM_FILTER_NAME, value: false, variant: "equalTo" },
];

type CommonProps = Omit<AdminManageRetailersTableProps, "isReady"> &
  Pick<
    FilterBarWithSearchProps,
    "FilterBarContainerComponent" | "FiltersContainerComponent"
  > & {
    defaultFilters?: AppliedFilter[];
    headerRight?: ReactNode;
  };

type BaseProps = Omit<CommonProps, "defaultFilters"> & {
  filters: AppliedFilter[];
  isTableReady?: boolean;
  onFiltersChange: (filters: AppliedFilter[]) => void;
};

function BaseAdminManageRetailersTable({
  FilterBarContainerComponent,
  FiltersContainerComponent,
  filter,
  filters,
  headerRight,
  isTableReady = true,
  onFiltersChange: handleFiltersChange,
  ...rest
}: BaseProps) {
  return (
    <>
      <FilterBarWithSearch
        key={String(isTableReady)}
        filterDefinitions={filterDefinitions}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        headerRight={
          <FilterBarControls
            isReady={isTableReady}
            filters={filters}
            onFiltersChange={handleFiltersChange}
          >
            {headerRight}
          </FilterBarControls>
        }
        {...{ FilterBarContainerComponent, FiltersContainerComponent }}
      />
      <AdminManageRetailersTable
        {...rest}
        filter={filter}
        isReady={isTableReady}
      />
    </>
  );
}

export default function AdminManageRetailersTableView({
  defaultFilters = DEFAULT_FILTERS,
  filter: filterProp,
  ...rest
}: CommonProps) {
  const { filter, filters, isTableReady, handleFiltersChange } =
    useGraphileFiltersUrlQuery<RetailerFilter>(filterDefinitions, {
      defaultFilters,
      externalQueryKeys: ["selectedId"],
    });

  const tableFilter = useOptionalCombineFilters(filterProp, filter);

  return (
    <BaseAdminManageRetailersTable
      {...{ filters, isTableReady }}
      {...rest}
      filter={tableFilter}
      onFiltersChange={handleFiltersChange}
    />
  );
}

export function EmbeddedAdminManageRetailersTable({
  defaultFilters = DEFAULT_FILTERS,
  filter: filterProp,
  ...rest
}: CommonProps) {
  const [filters, setFilters] = useState<AppliedFilter[]>(defaultFilters);

  const filterResults = useMemo(() => {
    if (!filters?.length) return;
    return getResults<RetailerFilter>(filterDefinitions, filters);
  }, [filters]);

  const tableFilter = useOptionalCombineFilters(filterProp, filterResults);

  return (
    <BaseAdminManageRetailersTable
      {...{ filters }}
      {...rest}
      {...FREESTANDING_TABLE_CONTAINER_PROPS}
      filter={tableFilter}
      onFiltersChange={setFilters}
      FilterBarContainerComponent={FreestandingFilterBarContainer}
      FiltersContainerComponent={FreestandingFilterWell}
    />
  );
}
